import {
  addDoc,
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  serverTimestamp,
  setDoc,
  updateDoc,
  where,
} from "firebase/firestore";
import { db } from "../firebase";
import {
  FOUNDING_SLOT_CAP,
  countFoundingApproved,
  getRateIfApproved,
} from "./feeLadder";

const POLL_MS = 15000;

export function subscribeApplications(onData, onError) {
  let cancelled = false;

  const load = async () => {
    try {
      const apps = await listApplications();
      if (!cancelled) onData(apps);
    } catch (err) {
      console.error("Applications load error:", err);
      if (!cancelled) onError?.(err);
    }
  };

  load();
  const timer = setInterval(load, POLL_MS);

  let cleaned = false;
  return () => {
    if (cleaned) return;
    cleaned = true;
    cancelled = true;
    clearInterval(timer);
  };
}

export async function listApplications() {
  const snap = await getDocs(collection(db, "applications"));
  return snap.docs
    .map((d) => ({ id: d.id, ...d.data() }))
    .filter((app) => app.status !== "auto-rejected");
}

async function resolveApprovalRate(existingApplications = []) {
  const foundingApproved = countFoundingApproved(existingApplications);
  return getRateIfApproved({
    foundingSlotsRemaining: FOUNDING_SLOT_CAP - foundingApproved,
  });
}

export async function approveApplication(id, reviewedBy) {
  const appRef = doc(db, "applications", id);
  const appSnap = await getDoc(appRef);
  if (!appSnap.exists()) {
    throw new Error("Application not found.");
  }

  const data = appSnap.data();
  const allSnap = await getDocs(
    query(collection(db, "applications"), where("status", "==", "approved"))
  );
  const approvedApps = allSnap.docs.map((d) => ({ id: d.id, ...d.data() }));
  const rate = await resolveApprovalRate(approvedApps);

  await updateDoc(appRef, {
    status: "approved",
    reviewedAt: serverTimestamp(),
    reviewedBy: reviewedBy || null,
    feeTier: rate.type,
    feeCohort: rate.cohort || rate.type,
    feeRate: rate.feeRate,
  });

  if (data.email) {
    const cleanEmail = data.email.trim().toLowerCase();
    await setDoc(
      doc(db, "waitlist_creators", cleanEmail),
      {
        approved: true,
        approvedAt: serverTimestamp(),
        email: cleanEmail,
        name: data.name || null,
        handle: data.handle || null,
        niche: data.niche || null,
        feeTier: rate.type,
        feeCohort: rate.cohort || rate.type,
        feeRate: rate.feeRate,
        ...(rate.type === "founding"
          ? {
              foundingCreator: true,
              isFoundingContributor: true,
              isFoundingMember: true,
            }
          : { foundingCreator: false }),
      },
      { merge: true }
    );
  }
}

export async function declineApplication(id, reviewedBy) {
  await updateDoc(doc(db, "applications", id), {
    status: "declined",
    reviewedAt: serverTimestamp(),
    reviewedBy: reviewedBy || null,
  });
}

export async function createApplication(payload) {
  return addDoc(collection(db, "applications"), {
    ...payload,
    status: payload.status || "pending",
    fansReferred: payload.fansReferred ?? 0,
    appliedAt: serverTimestamp(),
  });
}
